import React, { useMemo, Fragment, useState } from "react";
import { useQuery, gql } from "urql";
import { Link } from "wouter";
import { useQueryState } from "../../../components/src/use-query-state";
import { cloneDeep, sortBy } from "lodash";

import { defaultDate, DateForm } from "../../../components/src/dateForm";
import { dateToYYYYMMDDString } from "../../../components/src/common/Utils";
import { Page } from "../../../components/src/Page";
import { numberFormatFun } from "../../../common/src/utils/FormatFunctions";
import { TooltipLabel } from "../../../components/src/TooltipLabel";

import { PartyExternalAccountType, PartyType, PermissionAssetEnum } from "../types.generated";
import {
    SelectColumnFilter,
    SelectColumnFilterType,
    SelectNotZeroColumnFilter,
    notEquals
} from "../../../components/src/react-table/ReactTableFilters";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { GET_ME } from "../common/queries";

const GET_RECONCILE_QUANTITY = gql`
    query reconcilePositions($date: GraphQLDateString!) {
        reconcilePositions(date: $date) {
            id
            isCashAccount
            clientId
            client {
                name
                types
            }
            positionQuantity
            instrumentId
            instrument {
                name
                isin
                iban
                quantityDecimals
                type
                modelType
            }
            custodianId
            custodian {
                name
            }
            externalAccountId
            externalAccount {
                name
                type
            }
            custodianQuantity
            positionDate
            custodianDate
            custodianPositionId
            diff
            comment
            updateUserId
            updateUserInfo {
                name
            }
        }
    }
`;

export const PositionReconciliationPage = (): React.ReactElement => {
    const [endDate] = useQueryState("endDate", dateToYYYYMMDDString(defaultDate()));
    const [hideZerosChecked, setHideZerosChecked] = useState(true);
    const columns = useMemo(
        () => [
            {
                header: "Client",
                id: "client.name",
                accessorKey: "client.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return <Link to={`/parties/${row.original.clientId}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter,
                size: 170
            },
            {
                header: "Client type",
                id: "client.types",
                accessorKey: "client.types",
                accessorFn: (row) => (row.client ? row.client.types.join(", ") : null),
                filter: SelectColumnFilterType(PartyType),
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return <div>{row.original.client.types.join(", ")}</div>;
                    } else {
                        return null;
                    }
                },
                size: 170
            },
            {
                header: "Custodian",
                id: "custodian.name",
                accessorKey: "custodian.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.custodian) {
                        return <Link to={`/parties/${row.original.custodianId}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter,
                size: 170
            },
            {
                header: "Instrument",
                id: "instrument.name",
                accessorKey: "instrument.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                },
                size: 120
            },
            {
                header: "ISIN",
                id: "instrument.isin",
                accessorKey: "instrument.isin",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 120
            },
            {
                header: "IBAN",
                id: "instrument.iban",
                accessorKey: "instrument.iban",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 120
            },
            {
                header: "Model type",
                id: "instrument.modelType",
                accessorKey: "instrument.modelType",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                },
                size: 140,
                filter: SelectColumnFilter
            },
            {
                header: "Position date",
                accessorKey: "positionDate",
                size: 80
            },
            {
                header: "Custodian date",
                accessorKey: "custodianDate",
                filter: SelectColumnFilter,
                size: 80
            },
            {
                header: "External account id",
                id: "externalAccount.name",
                accessorKey: "externalAccount.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client && row.original.externalAccount) {
                        return (
                            <Link to={`/parties/${row.original.clientId}/externalAccounts/${row.original.externalAccountId}`}>
                                {row.original.externalAccount.name}
                            </Link>
                        );
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter,
                size: 170
            },
            {
                header: "Position quantity",
                accessorKey: "positionQuantity",
                filterFn: "startsWith",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <div style={{ textAlign: "right" }}>
                            {numberFormatFun("# ##0." + "#".repeat(row.original.instrument.quantityDecimals))(cellProps.getValue())}
                        </div>
                    );
                },
                size: 120
            },
            {
                header: "Custodian quantity",
                accessorKey: "custodianQuantity",
                filterFn: "startsWith",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <div style={{ textAlign: "right" }}>
                            {numberFormatFun("# ##0." + "#".repeat(row.original.instrument.quantityDecimals))(cellProps.getValue())}
                        </div>
                    );
                },
                size: 120
            },
            {
                header: "Diff",
                accessorKey: "diff",
                filter: SelectNotZeroColumnFilter,
                filterFn: notEquals,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <div style={{ textAlign: "right" }}>
                            {numberFormatFun("# ##0." + "#".repeat(row.original.instrument.quantityDecimals))(cellProps.getValue())}
                        </div>
                    );
                },
                size: 100
            },
            {
                header: "#",
                accessorKey: "custodianPositionId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.custodianPositionId) {
                        return <Link to={`/reconciliation/custodianpositions/${row.original.custodianPositionId}`}>{"#"}</Link>;
                    } else {
                        return null;
                    }
                },
                size: 30
            },
            {
                header: "Comment",
                accessorKey: "comment",
                filter: SelectColumnFilter
            },
            {
                header: "Updated by",
                id: "updateUserInfo.name",
                accessorKey: "updateUserInfo.name",
                accessorFn: (row) => (row.updateUserInfo ? row.updateUserInfo.name : null),
                filter: SelectColumnFilter
            }
        ],
        []
    );

    const [{ fetching, error, data }] = useQuery({
        query: GET_RECONCILE_QUANTITY,
        requestPolicy: "network-only",
        variables: { dateSweden: endDate, date: endDate }
    });

    const [{ fetching: loadingMe, error: errorMe, data: dataMe }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    if (fetching || loadingMe)
        return (
            <Page>
                <div>
                    <div>Loading reconciliation</div>
                </div>
            </Page>
        );
    if (error) return <div>`Error! ${error.message}`</div>;
    if (errorMe) return <div>`Error! ${errorMe.message}`</div>;

    const clonedData = cloneDeep(data);

    let positions: any[] = clonedData.reconcilePositions;
    //positions = positions.filter((position) => position.isCashAccount !== true); // else isCashAccount===null get filtered
    positions = positions.filter(
        (position) =>
            position.externalAccount.type === PartyExternalAccountType.SecuritiesAccount ||
            position.externalAccount.type === PartyExternalAccountType.Equity ||
            position.externalAccount.type === PartyExternalAccountType.CashAccount
    );
    if (hideZerosChecked) {
        positions = positions.filter((position) => !(position.custodianQuantity === 0 && position.diff === 0));
    }
    const result = sortBy(positions, "clientId", "externalAccountId", "instrument.name");

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-8/12 px-2 mb-2 flex">
                    <DateForm defaultDateString={endDate} dateName="endDate"></DateForm>
                </div>
                <div className="w-full md:w-4/12 px-2 mb-4 text-right">
                    {dataMe &&
                    dataMe.me &&
                    dataMe.me.frontendRole &&
                    dataMe.me.frontendRole.assets &&
                    (dataMe.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) ||
                        dataMe.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/custody_reconciliation.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2 mb-3">
                <div className="w-full px-2 mb-4">
                    <div>Please note that positions posted or held as collateral are handled elsewhere</div>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <div className="flex items-baseline gap-4 mb-2">
                        <p className="mb-0">Number of positions: {result.length}</p>
                        <div className="form-check flex items-center">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={hideZerosChecked}
                                onChange={(e) => setHideZerosChecked(e.target.checked)}
                                id="hideZerosCheckbox"
                                style={{ marginTop: "0.125rem" }}
                            />
                            <label className="form-check-label ml-2 mb-0" htmlFor="hideZerosCheckbox" style={{ lineHeight: "1.5" }}>
                                <TooltipLabel
                                    text="Hide zeros"
                                    content="Hides positions with zero custodian quantity and zero difference"
                                    className="d-inline"
                                />
                            </label>
                        </div>
                    </div>
                    <ReactTable columns={columns} data={result} defaultHiddenColumns={[]} exportToXlsxFile="PositionReconciliation.xlsx" />
                </div>
            </div>
        </Fragment>
    );
};
